import { callApi } from "@src/utils/api-client";
import ClientLogsService from "./client-logs.service";
import {
  buildEnvironmentVariableConfig,
  buildEnvironmentVariablesConfig,
} from "./configs/environment-config.config";

const hasErrorResponse = (value: unknown): boolean => {
  return Boolean((value as { error?: string })?.error);
};

export async function getEnvironmentVariable(envVarName: string) {
  try {
    const { data } = await callApi(buildEnvironmentVariableConfig(envVarName));
    return data;
  } catch (error) {
    ClientLogsService.logError(
      "system",
      "environment-config.service.getEnvironmentVariable",
      `Error fetching environment variable: ${envVarName}`,
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "EnvironmentConfigAPI",
        functionName: "getEnvironmentVariable",
      },
    );
    return { error: error?.message || "Failed to fetch environment variable" };
  }
}

export async function getEnvironmentVariables(envVarNames: string[]) {
  try {
    const { data: rawData } = await callApi(buildEnvironmentVariablesConfig(envVarNames));
    const data = rawData;

    if (Array.isArray(data)) {
      return data.reduce<Record<string, unknown>>((acc, entry) => {
        const name = (entry as { name?: unknown })?.name;
        const value = (entry as { value?: unknown })?.value;
        const error = (entry as { error?: unknown })?.error;

        if (typeof name !== "string" || !name.trim()) {
          return acc;
        }

        acc[name] = error ? undefined : value;
        return acc;
      }, {});
    }

    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return data;
    }

    const sanitizedData = Object.entries(data as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((acc, [key, value]) => {
      acc[key] = hasErrorResponse(value) ? undefined : value;
      return acc;
    }, {});
    return sanitizedData;
  } catch (error) {
    ClientLogsService.logError(
      "system",
      "environment-config.service.getEnvironmentVariables",
      "Error fetching environment variables",
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "EnvironmentConfigAPI",
        functionName: "getEnvironmentVariables",
      },
    );
    return { error: error?.message || "Failed to fetch environment variables" };
  }
}
